/**
 * Opportunities (customer side of the customer <-> account manager flow). Behind the server-evaluated flag
 * `crm.opportunities` (default off): off -> the ordinary not-found screen and no nav entry.
 *  /opportunities      list + "new requirements" (requirement gathering, saved as a draft); a workspace with an agency
 *                      can send its requirements to Quixo or to that agency
 *  /opportunities/:id  draft: edit + final submit; afterwards: the quotation, choose services/modules, then
 *                      accept, reject, or negotiate (budget + required final price); timeline.
 * The screens live in features/opportunities (shared with the partner portal); this file is the customer frame.
 */
import { useMemo, type ReactNode } from 'react';
import { Heading, Loading, PageHeader } from '@qm/ui';
import { OPPORTUNITIES_FLAG } from '@qm/contracts/opportunities';
import { useApp } from '../../../lib/app-context';
import { bff, BffError } from '../../../lib/bff';
import { useFlag } from '../../../lib/flags';
import { NotFoundScreen } from '../../../shell/RouteStates';
import { OpportunitiesProvider, type OppEnv, type ScreenProps } from '../../../features/opportunities/env';
import { BuyerDetailScreen, BuyerListScreen } from '../../../features/opportunities/Buyer';

function Frame({ title, subtitle, eyebrow, actions, children }: ScreenProps) {
  return (
    <section className="qm-page" aria-labelledby="page-title">
      <PageHeader eyebrow={eyebrow} title={title} intro={subtitle} actions={actions} />
      {children}
    </section>
  );
}

/** The customer environment: customer routes, the shell translator, links inside /opportunities. */
export function CustomerOpportunities({ children }: { children: ReactNode }) {
  const { t, locale } = useApp();
  const env = useMemo<OppEnv>(() => ({
    variant: 'customer', t, locale, roles: [],
    http: { get: (p) => bff(p), send: (method, p, body) => bff(p, { method, body: body === undefined ? undefined : JSON.stringify(body) }) },
    failure: (e) => (e instanceof BffError
      ? { status: e.status, reason: (e.envelope?.error.details as { reason?: string } | undefined)?.reason, message: e.envelope?.error.message }
      : { status: 0 }),
    apiBase: '/v1/opportunities',
    links: { list: '/opportunities', detail: (id) => `/opportunities/${encodeURIComponent(id)}`, staff: '/opportunities' },
    Screen: Frame,
    can: { sell: () => false, assign: () => false, manage: () => false, actAs: () => false },
  }), [t, locale]);
  return <OpportunitiesProvider env={env}>{children}</OpportunitiesProvider>;
}

function Gate({ children }: { children: ReactNode }) {
  const { t } = useApp();
  const state = useFlag(OPPORTUNITIES_FLAG);
  if (state === 'off') return <NotFoundScreen />;
  if (state === 'loading') return <section className="qm-page"><Heading level={1}>{t('opportunities.title')}</Heading><Loading label={t('common.loading')} /></section>;
  return <CustomerOpportunities>{children}</CustomerOpportunities>;
}

export function OpportunitiesPage() { return <Gate><BuyerListScreen /></Gate>; }
export function OpportunityDetailPage() { return <Gate><BuyerDetailScreen /></Gate>; }
